import { Routes, Route } from 'react-router-dom';
import { AngularHost } from './angular/AngularHost';
import { AngularLeafHost } from './angular/AngularLeafHost';
import { DssShell } from './dss/DssShell';
import { LayoutShell } from './layout/LayoutShell';
import { ChromelessShell } from './layout/ChromelessShell';
import { BrandedShell } from './layout/BrandedShell';
import { BareShell } from './layout/BareShell';
import { RequireAuth } from './layout/RequireAuth';
import { PostLoginRedirect } from './layout/PostLoginRedirect';
import {
  MIGRATED_ROUTES,
  MigratedRoute,
  migrationFlagKey,
  moduleSlug,
  toDssPath,
} from './migrated-routes';
import { ModuleGate } from './migration/ModuleGate';

// Resolve o shell React de uma rota migrada e entrega o conteúdo como children:
// - 'chromeless': só o conteúdo, sem chrome e sem gate (tela pública, ex.: softphone).
// - 'bare': só o conteúdo, sem chrome, mas sob RequireAuth e com skin/DSS no body
//   (tela autenticada que já traz o próprio enquadramento, ex.: wizard).
// - 'branded': faixa lateral só com a logo, sob RequireAuth (telas pré-tenant do login).
// - default: layout privado completo (sidebar/navbar), sob RequireAuth.
// RequireAuth é uma leitura síncrona de token (sem HTTP), então não reintroduz as
// chamadas tenant-scoped que motivaram os shells alternativos.
function shellElement(shell: MigratedRoute['shell'], content: JSX.Element): JSX.Element {
  if (shell === 'chromeless') {
    return <ChromelessShell>{content}</ChromelessShell>;
  }
  return <RequireAuth>{privateShell(shell, content)}</RequireAuth>;
}

function privateShell(shell: MigratedRoute['shell'], content: JSX.Element): JSX.Element {
  if (shell === 'bare') {
    return <BareShell>{content}</BareShell>;
  }
  return shell === 'branded' ? (
    <BrandedShell>{content}</BrandedShell>
  ) : (
    <LayoutShell>{content}</LayoutShell>
  );
}

// Rota sem página React: nenhum gate e nenhuma leitura de flag, a folha serve em
// qualquer estado. Rota com página passa pelo gate, que cai na folha quando o módulo
// não está migrado.
function routeContent(route: MigratedRoute): JSX.Element {
  if (!route.page) {
    return <AngularLeafHost />;
  }
  const Page = route.page;
  return (
    <ModuleGate
      flagKey={migrationFlagKey(route)}
      scope={moduleSlug(route)}
      page={<Page />}
      fallback={<AngularLeafHost />}
    />
  );
}

export function App() {
  return (
    <Routes>
      {/* '/' é a landing pós-login (destino do redirect do goHome legado): resolve
          a home real do usuário e redireciona, em vez de montar uma folha. */}
      <Route
        path="/"
        element={
          <RequireAuth>
            <PostLoginRedirect />
          </RequireAuth>
        }
      />
      {MIGRATED_ROUTES.filter(({ path }) => path !== '/').map((route) => (
        <Route
          key={route.path}
          path={route.path}
          element={shellElement(route.shell, routeContent(route))}
        />
      ))}
      {/* Gêmeo DSS de cada rota migrada: mapeamento incondicional, independente do
          campo `shell` — DSS sempre vai para DssShell, nunca LayoutShell/ChromelessShell.
          O gêmeo usa o mesmo objeto de rota: chave da flag e página saem iguais por
          construção. */}
      {MIGRATED_ROUTES.map((route) => (
        <Route
          key={`dss:${route.path}`}
          path={toDssPath(route.path)}
          element={<DssShell>{routeContent(route)}</DssShell>}
        />
      ))}
      <Route path="*" element={<AngularHost />} />
    </Routes>
  );
}
